import { ConsultationStatus } from '@/models/Consultation';
import { DrugStatus } from '@/models/Drug/Drug';
import { OrderStatus } from '@/models/Order';
import { PriorAuthorizationStatusCode } from '@/models/PriorAuthorization';
import { RefillStatusCode } from '@/models/Prescription';
import { taskStatusLabel } from '@/models/UserTask';

/**
 * The status enum each entity stores in its own Status column.
 *
 * A type that is absent from this map has no numeric status of its own, so its raw code is
 * shown undecoded. That matters: RefillStatusCode covers 0-11 densely, so decoding an
 * unrelated entity against it never fails loudly, it just produces a wrong-but-plausible
 * label. A bare number is the honest answer.
 */
const STATUS_LABELLERS: Record<string, (statusCode: number) => string | undefined> = {
    Prescription: code => RefillStatusCode[code],
    Order: code => OrderStatus[code],
    PriorAuthorization: code => PriorAuthorizationStatusCode[code],
    Drug: code => DrugStatus.DrugStatusEnum[code],
    Task: code => taskStatusLabel(code),
    Consultation: code => ConsultationStatus[code],
    // PrescriberContactTemplateTriggers.PrescriptionStatus is a RefillStatusCode, not a status
    // of its own kind; PrescriptionSubstatus is intercepted earlier by the substatus branch.
    'Prescriber/contact-templates/triggers': code => RefillStatusCode[code],
};

/**
 * Renders a history Status value as `<code> (<name>)`, decoded against the enum belonging to
 * the entity type being viewed. Falls back to the bare code when the type has no mapping or
 * the code is not a member of its enum, and to an empty string for a non-numeric value.
 *
 * @param entityType the History page's active search type (its `value`, else its `label`)
 */
export function formatHistoryStatus(entityType: string | null | undefined, statusCode: number): string {
    if (statusCode == null || isNaN(statusCode)) return '';

    const statusName = entityType && Object.hasOwn(STATUS_LABELLERS, entityType)
        ? STATUS_LABELLERS[entityType](statusCode)
        : undefined;
    if (statusName) return `${ statusCode } (${ statusName })`;

    return statusCode.toString();
}
